import { ENVIRONMENT } from '@config/environment';
import canvasSize from 'canvas-size';

const canvasAsBlob = (
  canvas: HTMLCanvasElement,
  mimeType = 'image/jpeg',
  quality = 1
): Promise<Blob | null> => {
  return new Promise(resolve => {
    canvas.toBlob(
      (blob: Blob | null) => {
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
};

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();

      if (event.target) {
        img.src = event.target.result as string;
        img.onload = () => {
          resolve(img);
        };
      }
      img.onerror = error => reject(error);
    };
  });
};

const contextToJPEG = async (
  ctx: CanvasRenderingContext2D,
  mimeType = 'image/jpeg',
  quality = 1
): Promise<File> => {
  const blob = await canvasAsBlob(ctx.canvas, mimeType, quality);

  if (!blob) throw new Error('Image Compression: Blob is null');

  const newFile = new File([blob], 'image.jpg', {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  return newFile;
};

const imageToCTX = (
  img: HTMLImageElement,
  width: number,
  height: number
): CanvasRenderingContext2D => {
  const elem = document.createElement('canvas');
  elem.width = width;
  elem.height = height;
  const ctx = elem.getContext('2d');

  if (!ctx) throw new Error('Image Compression: Canvas context is null');
  ctx.imageSmoothingEnabled = true;

  ctx.drawImage(img, 0, 0, width, height);
  return ctx;
};

const imageScale = (
  img: HTMLImageElement,
  maxSize: number
): CanvasRenderingContext2D | null => {
  const scaleFactor = Math.min(maxSize / img.width, maxSize / img.height);

  if (scaleFactor > 1) {
    return null;
  }

  const ctx = imageToCTX(
    img,
    img.width * scaleFactor,
    img.height * scaleFactor
  );

  if (!ctx) return null;

  return ctx;
};

export const imageCompression = async (
  file: File,
  maxSize = 1680
): Promise<File | null> => {
  const img = await loadImage(file);

  if (img.width < maxSize && img.height < maxSize) {
    console.debug(
      `Image Compression: Image is smaller than max size of ${maxSize} ${img.width}x${img.height} no need to compress`
    );
    return file;
  }

  const { success } = await canvasSize.test({
    height: img.height,
    width: img.width,
  });
  if (!success) {
    const { width, height } = await canvasSize.maxArea({ max: 16384 });
    const errorMessage = `Image resolution ${img.width} x ${img.height} is not supported. Max resolution is ${width} x ${height} pixels.`;
    throw new Error(errorMessage);
  }

  const imageCTX = imageToCTX(img, img.width, img.height);
  const compressedFile = await contextToJPEG(imageCTX, file.type, 0.75);
  const compressedImage = await loadImage(compressedFile);
  const compressedScaledImage = imageScale(compressedImage, maxSize);

  if (!compressedScaledImage) {
    throw new Error('Could not scale image');
  }

  const compressedScaledFile = await contextToJPEG(compressedScaledImage);

  void debug(
    {
      original: `${file.size / 1000}kB`,
      scaled: 'N/A',
      compressed: `${compressedFile.size / 1000}kB`,
      compressedScaled: `${compressedScaledFile.size / 1000}kB`,
    },
    {
      original: file,
      compressed: compressedFile,
      compressedScaled: compressedScaledFile,
    },
    maxSize
  );

  if (compressedScaledFile.size > file.size) {
    console.debug(
      'Image Compression: Compressed image is larger than original, returning original'
    );
    return file;
  }

  return compressedScaledFile;
};

export const debug = async (
  {
    original,
    scaled,
    compressed,
    compressedScaled,
  }: {
    original: string;
    scaled: string;
    compressed: string;
    compressedScaled: string;
  },
  {
    original: originalFile,

    compressed: compressedFile,

    compressedScaled: compressedScaledFile,
  }: {
    original: File;
    compressed: File;
    compressedScaled: File;
  },
  maxSize: number,
  download = false
) => {
  if (ENVIRONMENT === 'development') {
    // Scale original image
    const img = await loadImage(originalFile);

    const scaledImage = imageScale(img, maxSize);
    if (scaledImage) {
      const scaledFile = await contextToJPEG(scaledImage);
      scaled = `${scaledFile.size / 1000}kB`;

      if (download) {
        await downloadFile(scaledFile, 'scaled');
      }
    }
  }

  console.debug(`Image Compression:`);
  console.debug('   original: ', original);
  console.debug('   scaled:', scaled);
  console.debug('   compressed: ', compressed);
  console.debug('   compressedScaled: ', compressedScaled);

  if (download) {
    await downloadFile(originalFile, 'original');
    await downloadFile(compressedFile, 'compressed');
    await downloadFile(compressedScaledFile, 'compressed-scaled');
  }
};

// Just used for testing... to check image quality
const downloadFile = (file: File, name: string) => {
  return new Promise(resolve => {
    const href = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = href;

    const fileExtension = file.name.split('.').pop();
    link.download = `${name}.${fileExtension}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);

    resolve(true);
  });
};
